/*
  Tiny markdown → HTML helper, scoped to fields stored by IIA with the
  `ui_element: "markdown"` config (currently only `modules/website/our_team`'s
  bio field — see modules/website/public/schema/our_team.yml). Those fields
  arrive at React as raw markdown text; rendering them straight through
  dangerouslySetInnerHTML shows literal `**bold**` / `[text](url)` / line
  breaks collapsed into one paragraph.

  This converter handles every formatting feature the IIA text editor
  (EasyMDE) toolbar can produce:
    B    → **bold** / __bold__ → <strong>
    I    → *italic* / _italic_ → <em>
    H    → `# ` / `## ` / `### ` → <h4> / <h5> / <h6> (demoted 3 levels
           because the team-member name already occupies h3)
    "    → `> text` (one or more consecutive lines) → <blockquote>
    ☰    → `- ` / `* ` line starts → <ul><li>
    ☰    → `1. ` line starts → <ol><li>
    🔗   → `[text](url)` → <a target="_blank" rel="noopener noreferrer">
    🖼    → `![alt](url)` → <img>
    blank line                          → paragraph break
    single newline inside a paragraph   → <br>

  The toolbar's other three buttons (Preview, Side-by-side, Fullscreen)
  are UI toggles and don't produce markdown output, so nothing to handle
  for them. Code blocks, tables, strikethrough and inline code are
  intentionally not handled — they're not in the IIA toolbar.

  XSS: input is escaped first, then inline markers are converted into tags
  on the escaped string. The output is safe to inject via
  dangerouslySetInnerHTML for trusted-admin content (the IIA team).
*/

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

function applyInline(s: string): string {
  // Links/images are rendered into placeholder tokens first, then swapped
  // back in after bold/italic run. Otherwise a URL containing two
  // underscores (extremely common — e.g. `?utm_source=x&utm_campaign=y`)
  // gets matched by the italic regex below and `<em>` tags get spliced into
  // the middle of the `href`/`src` attribute, corrupting the link. The
  // token uses \0, which escapeHtml never produces and the source editor
  // can't type, so it can't collide with real user text.
  const placeholders: string[] = []
  const stash = (html: string): string => {
    const token = `\0${placeholders.length}\0`
    placeholders.push(html)
    return token
  }

  // URL may be wrapped in escaped angle brackets — `(<url>)` is valid
  // CommonMark for an explicit link destination (commonly used when the
  // URL itself contains odd characters). escapeHtml has already turned
  // those into &lt;/&gt;, so strip that wrapper before use, or it ends up
  // literally inside the `href`/`src` attribute and breaks the link.
  const unwrapUrl = (url: string): string => url.replace(/^&lt;(.*)&gt;$/, '$1')

  // Image first — must come BEFORE the link rule because the image syntax
  // is `![alt](url)` and the link regex would otherwise gobble the `[alt](url)`
  // part and leave a stray `!` behind.
  s = s.replace(/!\[([^\]]*)\]\(([^)\s]+)\)/g, (_m, alt, url) =>
    stash(`<img src="${unwrapUrl(url)}" alt="${alt}" loading="lazy" decoding="async">`),
  )
  // [text](url) — URL is double-quoted in the output, and quotes inside the
  // URL would already have been escaped to &quot; by escapeHtml above.
  s = s.replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (_m, text, url) =>
    stash(`<a href="${unwrapUrl(url)}" target="_blank" rel="noopener noreferrer">${text}</a>`),
  )
  // Bold first (longer sequence) so `**word**` matches before `*word*`.
  s = s.replace(/(\*\*|__)(.+?)\1/g, '<strong>$2</strong>')
  // Italic — single * or _ around content not containing the same marker.
  s = s.replace(/(?<!\*)\*(?!\s)([^*]+?)(?<!\s)\*(?!\*)/g, '<em>$1</em>')
  s = s.replace(/(?<!_)_(?!\s)([^_]+?)(?<!\s)_(?!_)/g, '<em>$1</em>')

  s = s.replace(/\0(\d+)\0/g, (_m, i) => placeholders[Number(i)])
  return s
}

export function mdToHtml(md: string | null | undefined): string {
  if (!md) return ''
  const escaped = escapeHtml(md.trim())
  if (!escaped) return ''

  // Parse line-by-line. Headings, list items, and paragraph lines can
  // intermix without blank-line separators (which is how the IIA admin
  // tends to write — they don't always leave a blank line before a `#`
  // heading or a `-` list, which strict CommonMark would require).
  const out: string[] = []
  let paragraphLines: string[] = []
  let listType: 'ul' | 'ol' | null = null
  let listItems: string[] = []
  let quoteLines: string[] = []

  const flushParagraph = () => {
    if (paragraphLines.length === 0) return
    const body = paragraphLines.map(applyInline).join('<br>')
    if (body.trim() !== '') out.push(`<p>${body}</p>`)
    paragraphLines = []
  }
  const flushList = () => {
    if (listItems.length === 0 || listType === null) return
    const items = listItems.map((l) => `<li>${applyInline(l)}</li>`).join('')
    out.push(`<${listType}>${items}</${listType}>`)
    listItems = []
    listType = null
  }
  const flushQuote = () => {
    if (quoteLines.length === 0) return
    const body = quoteLines.map(applyInline).join('<br>')
    out.push(`<blockquote>${body}</blockquote>`)
    quoteLines = []
  }
  const flushAll = () => {
    flushParagraph()
    flushList()
    flushQuote()
  }

  for (const rawLine of escaped.split(/\n/)) {
    // Blank line → close any open block.
    if (/^\s*$/.test(rawLine)) {
      flushAll()
      continue
    }

    // Heading `# `, `## `, `### ` … followed by content. Demoted 3 levels
    // (`#` → h4) because the team-member name already occupies h3.
    const heading = rawLine.match(/^(#{1,3})\s+(.+)$/)
    if (heading) {
      flushAll()
      const level = Math.min(heading[1].length + 3, 6)
      out.push(`<h${level}>${applyInline(heading[2])}</h${level}>`)
      continue
    }

    // Lone heading marker with no text after (`#`, `##`) → drop silently.
    if (/^#{1,6}\s*$/.test(rawLine)) {
      continue
    }

    // Blockquote `> text` — consecutive `> ` lines fold into a single
    // <blockquote>. A non-quote line flushes the quote (handled below).
    // NB: escapeHtml above converted any `>` into `&gt;`, so the regex
    // matches the entity form, not the literal character.
    const quote = rawLine.match(/^&gt;\s?(.*)$/)
    if (quote) {
      if (paragraphLines.length > 0 || listType !== null) {
        flushParagraph()
        flushList()
      }
      quoteLines.push(quote[1])
      continue
    }

    // Unordered list item `- ` / `* `.
    const ul = rawLine.match(/^\s*[-*]\s+(.+)$/)
    if (ul) {
      if (listType !== 'ul') {
        flushParagraph()
        flushList()
        flushQuote()
        listType = 'ul'
      }
      listItems.push(ul[1])
      continue
    }

    // Ordered list item `1. `.
    const ol = rawLine.match(/^\s*\d+\.\s+(.+)$/)
    if (ol) {
      if (listType !== 'ol') {
        flushParagraph()
        flushList()
        flushQuote()
        listType = 'ol'
      }
      listItems.push(ol[1])
      continue
    }

    // Default — accumulate into the current paragraph. If we're in a
    // different block (list, quote), flush it first.
    if (listType !== null) flushList()
    if (quoteLines.length > 0) flushQuote()
    paragraphLines.push(rawLine)
  }

  flushAll()
  return out.join('')
}
